/* Browser bundle only: sql-wasm.js pulls node:crypto/fs. Explicit browser build + public/sql-wasm-browser.wasm. */
import initSqlJs, { type Database } from 'sql.js/dist/sql-wasm-browser.js';
import type { DudaDateGranularity } from './dudaApi';
import type { DudaAnalyticsCacheV1, DudaSiteAnalyticsSnapshot } from './dudaAnalyticsTypes';
import { DUDA_ANALYTICS_STORAGE_KEY } from './dudaAnalyticsTypes';
import { DUDA_ACCOUNT_PRIMARY_ID } from '../settings/dudaAccounts';

const IDB_NAME = 'growthhub_duda_sqlite';
const IDB_VER = 1;
const IDB_STORE = 'files';

let sqlModulePromise: ReturnType<typeof initSqlJs> | null = null;
const dbByAccount = new Map<string, Database>();
const openingByAccount = new Map<string, Promise<Database>>();

function wasmLocateFile(file: string): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/${file}`.replace(/\/+/g, '/');
}

function storageKeyForAccount(accountId: string): string {
  const safe = accountId.replace(/[^a-zA-Z0-9_-]/g, '_');
  return `duda_analytics_${safe || 'default'}`;
}

async function getSqlModule() {
  if (!sqlModulePromise) {
    sqlModulePromise = initSqlJs({ locateFile: wasmLocateFile });
  }
  return sqlModulePromise;
}

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VER);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const d = req.result;
      if (!d.objectStoreNames.contains(IDB_STORE)) {
        d.createObjectStore(IDB_STORE);
      }
    };
  });
}

async function idbLoad(accountId: string): Promise<ArrayBuffer | null> {
  const key = storageKeyForAccount(accountId);
  const idb = await openIdb();
  return new Promise((resolve, reject) => {
    const tx = idb.transaction(IDB_STORE, 'readonly');
    const r = tx.objectStore(IDB_STORE).get(key);
    r.onsuccess = () => {
      const v = r.result as ArrayBuffer | undefined;
      resolve(v && v.byteLength ? v : null);
    };
    r.onerror = () => reject(r.error ?? new Error('IndexedDB read failed'));
  });
}

async function idbSave(accountId: string, data: Uint8Array): Promise<void> {
  const key = storageKeyForAccount(accountId);
  const idb = await openIdb();
  const buf = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
  return new Promise((resolve, reject) => {
    const tx = idb.transaction(IDB_STORE, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB write failed'));
    tx.objectStore(IDB_STORE).put(buf, key);
  });
}

function ensureSchema(db: Database): void {
  db.run(`CREATE TABLE IF NOT EXISTS duda_cache_meta (
    id INTEGER PRIMARY KEY CHECK (id = 1),
    version INTEGER NOT NULL,
    downloaded_at TEXT NOT NULL,
    range_from TEXT NOT NULL,
    range_to TEXT NOT NULL,
    date_granularity TEXT NOT NULL
  )`);
  db.run(`CREATE TABLE IF NOT EXISTS duda_site_snapshot (
    site_name TEXT NOT NULL PRIMARY KEY,
    downloaded_at TEXT NOT NULL,
    range_from TEXT NOT NULL,
    range_to TEXT NOT NULL,
    date_granularity TEXT NOT NULL,
    traffic_summary TEXT,
    traffic_summary_error TEXT,
    traffic_geo TEXT,
    traffic_geo_error TEXT,
    traffic_system TEXT,
    traffic_system_error TEXT,
    activities TEXT,
    activities_error TEXT
  )`);
}

function writeCacheToDb(db: Database, cache: DudaAnalyticsCacheV1): void {
  db.run('DELETE FROM duda_site_snapshot');
  db.run('DELETE FROM duda_cache_meta');
  db.run(
    'INSERT INTO duda_cache_meta (id, version, downloaded_at, range_from, range_to, date_granularity) VALUES (1, ?, ?, ?, ?, ?)',
    [cache.version, cache.downloadedAt, cache.range.from, cache.range.to, cache.dateGranularity]
  );
  const insert = db.prepare(`INSERT INTO duda_site_snapshot (
    site_name, downloaded_at, range_from, range_to, date_granularity,
    traffic_summary, traffic_summary_error, traffic_geo, traffic_geo_error,
    traffic_system, traffic_system_error, activities, activities_error
  ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`);
  try {
    for (const snap of Object.values(cache.sites)) {
      insert.run([
        snap.siteName,
        snap.downloadedAt,
        cache.range.from,
        cache.range.to,
        cache.dateGranularity,
        snap.trafficSummary == null ? null : JSON.stringify(snap.trafficSummary),
        snap.trafficSummaryError ?? null,
        snap.trafficByGeo == null ? null : JSON.stringify(snap.trafficByGeo),
        snap.trafficByGeoError ?? null,
        snap.trafficBySystem == null ? null : JSON.stringify(snap.trafficBySystem),
        snap.trafficBySystemError ?? null,
        snap.activities == null ? null : JSON.stringify(snap.activities),
        snap.activitiesError ?? null,
      ]);
    }
  } finally {
    insert.free();
  }
}

function migrateLegacyLocalStorageIfNeeded(db: Database): boolean {
  const metaRes = db.exec('SELECT 1 FROM duda_cache_meta WHERE id = 1');
  if (metaRes.length > 0 && metaRes[0].values.length > 0) {
    return false;
  }
  try {
    const raw = localStorage.getItem(DUDA_ANALYTICS_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const cache = JSON.parse(raw) as DudaAnalyticsCacheV1;
    if (cache.version !== 1 || !cache.sites || typeof cache.sites !== 'object') {
      return false;
    }
    writeCacheToDb(db, cache);
    localStorage.removeItem(DUDA_ANALYTICS_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

function parseJsonCol(v: unknown): unknown | null {
  if (v == null || v === '') {
    return null;
  }
  if (typeof v !== 'string') {
    return v;
  }
  try {
    return JSON.parse(v) as unknown;
  } catch {
    return null;
  }
}

function resetDbSingleton(accountId: string): void {
  const db = dbByAccount.get(accountId);
  if (db) {
    try {
      db.close();
    } catch {
      /* ignore */
    }
    dbByAccount.delete(accountId);
  }
  openingByAccount.delete(accountId);
}

export async function getDudaAnalyticsDb(accountId: string): Promise<Database> {
  const existing = dbByAccount.get(accountId);
  if (existing) {
    return existing;
  }
  let p = openingByAccount.get(accountId);
  if (!p) {
    p = (async () => {
      const SQL = await getSqlModule();
      const buf = await idbLoad(accountId);
      const db = buf ? new SQL.Database(new Uint8Array(buf)) : new SQL.Database();
      ensureSchema(db);
      const migrated =
        accountId === DUDA_ACCOUNT_PRIMARY_ID ? migrateLegacyLocalStorageIfNeeded(db) : false;
      if (migrated) {
        await idbSave(accountId, db.export());
      }
      dbByAccount.set(accountId, db);
      openingByAccount.delete(accountId);
      return db;
    })();
    openingByAccount.set(accountId, p);
  }
  return p;
}

export async function loadDudaAnalyticsCacheAsync(accountId: string): Promise<DudaAnalyticsCacheV1 | null> {
  const db = await getDudaAnalyticsDb(accountId);
  const metaRes = db.exec('SELECT version, downloaded_at, range_from, range_to, date_granularity FROM duda_cache_meta WHERE id = 1');
  if (!metaRes.length || !metaRes[0].values.length) {
    return null;
  }
  const row0 = metaRes[0].values[0];
  const version = Number(row0[0]);
  const downloadedAt = String(row0[1]);
  const rangeFrom = String(row0[2]);
  const rangeTo = String(row0[3]);
  const dateGranularity = String(row0[4]) as DudaDateGranularity;

  const stmt = db.prepare('SELECT * FROM duda_site_snapshot');
  const sites: Record<string, DudaSiteAnalyticsSnapshot> = {};
  try {
    while (stmt.step()) {
      const row = stmt.getAsObject();
      const siteName = String(row.site_name ?? '');
      if (!siteName) {
        continue;
      }
      sites[siteName] = {
        siteName,
        downloadedAt: String(row.downloaded_at ?? ''),
        range: { from: String(row.range_from ?? rangeFrom), to: String(row.range_to ?? rangeTo) },
        dateGranularity: String(row.date_granularity ?? dateGranularity) as DudaDateGranularity,
        trafficSummary: parseJsonCol(row.traffic_summary),
        trafficSummaryError: row.traffic_summary_error ? String(row.traffic_summary_error) : undefined,
        trafficByGeo: parseJsonCol(row.traffic_geo),
        trafficByGeoError: row.traffic_geo_error ? String(row.traffic_geo_error) : undefined,
        trafficBySystem: parseJsonCol(row.traffic_system),
        trafficBySystemError: row.traffic_system_error ? String(row.traffic_system_error) : undefined,
        activities: parseJsonCol(row.activities),
        activitiesError: row.activities_error ? String(row.activities_error) : undefined,
      };
    }
  } finally {
    stmt.free();
  }

  return {
    version: version as 1,
    downloadedAt,
    range: { from: rangeFrom, to: rangeTo },
    dateGranularity,
    sites,
  };
}

export async function saveDudaAnalyticsCacheAsync(
  accountId: string,
  cache: DudaAnalyticsCacheV1
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const db = await getDudaAnalyticsDb(accountId);
    writeCacheToDb(db, cache);
    await idbSave(accountId, db.export());
    return { ok: true };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

export async function clearDudaAnalyticsSqliteAsync(accountId: string): Promise<void> {
  const db = await getDudaAnalyticsDb(accountId);
  db.run('DELETE FROM duda_site_snapshot');
  db.run('DELETE FROM duda_cache_meta');
  await idbSave(accountId, db.export());
  if (accountId === DUDA_ACCOUNT_PRIMARY_ID) {
    localStorage.removeItem(DUDA_ANALYTICS_STORAGE_KEY);
  }
}

export async function getPersistedSqliteByteSizeAsync(accountId: string): Promise<number> {
  const buf = await idbLoad(accountId);
  return buf?.byteLength ?? 0;
}

export async function exportDudaSqliteUint8Array(accountId: string): Promise<Uint8Array> {
  const db = await getDudaAnalyticsDb(accountId);
  return db.export();
}

export async function importDudaSqliteArrayBuffer(accountId: string, buffer: ArrayBuffer): Promise<void> {
  resetDbSingleton(accountId);
  const SQL = await getSqlModule();
  const db = new SQL.Database(new Uint8Array(buffer));
  ensureSchema(db);
  await idbSave(accountId, db.export());
  db.close();
}
